import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Badge, Spinner, Button, Input } from 'reactstrap';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';

const SystemLogs = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [health, setHealth] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [activeType, setActiveType] = useState('DailyPredictions');
  const [hideScoring, setHideScoring] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const [hRes, jRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/cron-health/health`),
        fetch(`${API_BASE_URL}/api/jobs/executions?limit=20&job_name=${encodeURIComponent(activeType)}`)
      ]);
      const hJson = await hRes.json();
      const jJson = await jRes.json();
      setHealth(hJson);
      const jobsArray = Array.isArray(jJson?.data)
        ? jJson.data
        : Array.isArray(jJson?.executions)
        ? jJson.executions
        : Array.isArray(jJson)
        ? jJson
        : [];
      setJobs(jobsArray);
    } catch (e) {
      setErr('Failed to load system health/logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [activeType]);

  const typeOrder = ['DailyPredictions', '4HourPredictions', 'DailyReportScreenshots', 'DailyVideoAutomation', 'ExpiredPredictionScoring'];
  const typeLabels = {
    DailyPredictions: 'Daily Predictions',
    '4HourPredictions': '4h Predictions',
    DailyReportScreenshots: 'Report Screenshots',
    DailyVideoAutomation: 'Daily Video',
    ExpiredPredictionScoring: 'Scoring'
  };

  // If Scoring tab is active, do not hide scoring even if toggle is on
  const filtered = useMemo(() => {
    if (hideScoring && activeType !== 'ExpiredPredictionScoring') {
      return jobs.filter(j => j.job_name !== 'ExpiredPredictionScoring');
    }
    return jobs;
  }, [jobs, hideScoring, activeType]);
  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of typeOrder) map.set(t, []);
    for (const j of filtered) {
      if (typeOrder.includes(j.job_name)) {
        map.get(j.job_name).push(j);
      }
    }
    return map;
  }, [filtered]);

  useEffect(() => {
    // keep default to first non-scoring type if somehow reset
  if (!activeType || !typeOrder.includes(activeType)) setActiveType('DailyPredictions');
  }, [grouped]);

  // Map backend health statuses to badge colors
  const statusColor = (s) => {
    if (s === 'healthy') return 'success';
    if (s === 'warning' || s === 'degraded') return 'warning';
    if (s === 'critical' || s === 'error') return 'danger';
    if (s === 'idle' || s === 'unknown') return 'secondary';
    return 'secondary';
  };
  const jobStatusColor = (s) => s === 'succeeded' ? 'success' : (s === 'running' ? 'info' : 'danger');
  const fmt = (d) => d ? new Date(d).toLocaleString() : '';

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div>
                <h4 className="mb-sm-0">System Logs</h4>
                <p className="text-muted mb-0">Cron health and recent job activity</p>
              </div>
              <div>
                <Button color="primary" size="sm" onClick={load} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Refresh'}
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {err && (
          <Row><Col><div className="alert alert-danger">{err}</div></Col></Row>
        )}

        <Row>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h5 className="mb-0">Cron Health</h5>
              </CardHeader>
              <CardBody>
                {!health ? (
                  <div className="text-muted">No data</div>
                ) : (
                  <>
                    <div className="mb-3">
                      Overall: <Badge color={statusColor(health.status)}>{(health.status || '').toUpperCase()}</Badge>
                    </div>
                    <div className="table-responsive">
                      <Table hover size="sm" className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Job</th>
                            <th>Status</th>
                            <th>Schedule</th>
                            <th>Runs</th>
                            <th>Success Rate</th>
                            <th>Consec. Failures</th>
                            <th>Last Run</th>
                            <th>Last Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(health.jobs || []).map((j, idx) => (
                            <tr key={idx}>
                              <td className="text-nowrap">{j.name}</td>
                              <td><Badge color={statusColor(j.status)}>{(j.status || '').toUpperCase()}</Badge></td>
                              <td className="text-nowrap">{j.schedule || ''}</td>
                              <td>{j.totalRuns}</td>
                              <td>{j.successRate != null ? `${j.successRate}%` : (j.totalRuns > 0 ? '' : '—')}</td>
                              <td className="text-danger">{j.consecutiveFailures ?? ''}</td>
                              <td className="text-nowrap">{fmt(j.lastRun)}</td>
                              <td style={{maxWidth: 260}}>
                                <small className="text-muted text-truncate d-inline-block" style={{maxWidth: 250}}>{j.lastError || ''}</small>
                              </td>
                            </tr>
                          ))}
                          {(!health.jobs || health.jobs.length === 0) && (
                            <tr><td colSpan="8" className="text-center text-muted">No jobs registered</td></tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">Recent Job Executions</h5>
                  <div className="form-check mb-0">
                    <Input id="sys-hide-scoring" type="checkbox" className="form-check-input" checked={hideScoring} onChange={(e) => setHideScoring(e.target.checked)} />
                    <label className="form-check-label" htmlFor="sys-hide-scoring">Hide Scoring</label>
                  </div>
                </div>
                <div className="mt-3">
                  <Nav tabs>
                    {typeOrder.map((t) => {
                      const key = t;
                      const label = typeLabels[t] || t;
                      const count = (grouped.get(t)?.length || 0);
                      return (
                        <NavItem key={key}>
                          <NavLink className={activeType === key ? 'active' : ''} onClick={() => setActiveType(key)} role="button">
                            {label} <Badge pill color="secondary" className="ms-1">{count}</Badge>
                          </NavLink>
                        </NavItem>
                      );
                    })}
                  </Nav>
                </div>
              </CardHeader>
              <CardBody className="p-0">
                <div className="table-responsive">
                  <TabContent activeTab={activeType}>
                    {Array.from(grouped.keys()).map((t) => (
                      <TabPane tabId={t} key={t}>
                        <Table hover className="mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Status</th>
                              <th>Job</th>
                              <th>Started</th>
                              <th>Duration</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(grouped.get(t) || []).map((j, i) => (
                              <tr key={`${j.id || i}-${j.started_at || i}`}>
                                <td><Badge color={jobStatusColor(j.status)}>{(j.status || '').toUpperCase()}</Badge></td>
                                <td className="text-nowrap">{j.job_name}</td>
                                <td className="text-nowrap">{fmt(j.started_at)}</td>
                                <td>{j.duration_seconds != null ? `${j.duration_seconds}s` : ''}</td>
                              </tr>
                            ))}
                            {(grouped.get(t)?.length || 0) === 0 && (
                              <tr><td colSpan="4" className="text-center text-muted py-3">No jobs for this type</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </TabPane>
                    ))}
                  </TabContent>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

      </Container>
    </div>
  );
};

export default SystemLogs;
